/**
 * FormRenderer Component
 * Renders complete form with all controls and validation error display
 *
 * Features:
 * - Field-level validation error display with accessibility
 * - Validation summary at form top
 * - Real-time error clearing on successful input
 * - Proper ARIA attributes for screen readers
 * - Touch state management for error display
 * - Conditional visibility based on form values and operating mode
 * - Support for expression-based, dependency-based, and mode-based visibility rules
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/controls.css';
import '../styles/form-validation.css';
import '../styles/form-content-styles.css';
import { FormField, type FormFieldProps } from './FormField';
import { FormSection } from './FormSection';
import '../styles/form-section.css';
import type { FormSchema, FormControl, OperatingMode as FormOperatingMode, FormValidationResult } from '@atlas-forms/types-js';
import type { ParentMap } from '@atlas-forms/types-js';
import { FormEngine } from '@atlas-forms/form-engine-js';
import {
  filterSchemaByVisibility,
  SafeExpressionEvaluator,
  getControlVisibilityStates,
} from '@atlas-forms/schema-js';
import { FormOperatingModeProvider } from '../contexts/FormOperatingModeContext';
import { getCsrfToken, getCsrfTokenHeaderName } from '@atlas-forms/client-js';
import { getModeConfig } from '../utils/modeConfig';
import { ScriptExecutor } from '@atlas-forms/controls-styling-schema-js';
import { useResolvedSchema } from '../control-inheritance';
import { useFormScopePartition } from '../hooks/useFormScopePartition';
import { FormScopeProcessor } from './FormScopeProcessor';
import { buildParentMap, resolveStyles, stylePropertiesToCSS, isEmptyStyleProperties } from '../styles/styleUtils';
import { collectChildFormErrors } from '../utils/childFormValidation';
import { useChildFormRegistryStore, childFormKey } from '@atlas-forms/form-engine-js';
import { containerChildFormRefs, loadChildFormSchemas, resolveChildData } from '../utils/childFormLoader';

/** True when a control, or a control nested inside a layout control, has a data binding */
function schemaHasBindings(controls: FormControl[] | undefined): boolean {
  return (controls ?? []).some(
    (control) =>
      !!control.binding ||
      schemaHasBindings((control.config?.controls as FormControl[] | undefined))
  );
}

/**
 * Shallow, key-by-key equality for the `initialValues` sync effect below — same "stable
 * value beats stable reference" rule this file's own `memoValues` (see the `values` state
 * memoization further down) already applies, extended to the `initialValues` *prop* a caller
 * hands in. A caller that rebuilds `initialValues` as a brand-new object literal on every
 * render (a real, live pattern: `@app-studio/widget-form`'s `FormWidgetRenderer.tsx` merges
 * its `props.initialData` — itself freshly recomputed by the widget engine's `render()` call
 * on every widget re-render, by that engine's own design — with locally-tracked `formValues`
 * via a fresh `{ ...a, ...b }` spread every render) must not force the sync effect below to
 * refire, and hence its own `onValuesChange` callback to fire, on every single render even
 * when nothing in `initialValues` actually changed.
 * @private
 */
function isShallowEqualRecord(a: Record<string, any> | undefined, b: Record<string, any> | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
}

function getFieldGridStyle(control: FormControl, totalColumns: number): React.CSSProperties {
  const style: React.CSSProperties = {};
  if (control.forceNewRow) style.gridColumnStart = 1;

  const gp = control.gridPosition;
  if (gp?.column) style.gridColumnStart = gp.column;

  const span = gp?.colSpan ?? control.columnSpan;
  if (span && span > 1) {
    style.gridColumn = `span ${Math.min(span, totalColumns)}`;
    if (gp?.column) style.gridColumnStart = gp.column;
  } else if (control.width) {
    switch (control.width) {
      case 'full':    style.gridColumn = '1 / -1'; break;
      case 'half':    style.gridColumn = `span ${Math.max(1, Math.ceil(totalColumns / 2))}`; break;
      case 'third':   style.gridColumn = `span ${Math.max(1, Math.ceil(totalColumns / 3))}`; break;
      case 'quarter': style.gridColumn = 'span 1'; break;
    }
  }

  return style;
}

export interface FormRendererProps {
  schema: FormSchema;
  initialValues?: Record<string, any>;
  /**
   * External data exposed to bindings as `$json` (e.g. a Data Table bound to `$json.leads`).
   * It is not a field value: it is only read when the form's bindings are resolved.
   */
  externalData?: Record<string, any>;
  mode?: FormOperatingMode;
  onSubmit?: (values: Record<string, any>, headers?: Record<string, string>) => Promise<void> | void;
  onClose?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  showValidationSummary?: boolean;
  /** Custom expression evaluator for visibility rules */
  expressionEvaluator?: any;
  /** Callback when controls become visible/hidden */
  onVisibilityChange?: (visibleControlIds: string[]) => void;
  /** Enable CSRF token protection on form submission */
  enableCsrfProtection?: boolean;
  /** Called on every field value change — use for real-time data sync (e.g. child forms in a container) */
  onValuesChange?: (values: Record<string, any>) => void;
  /** Called once the form's engine exists — a FormContainer uses it to validate this child form */
  onEngineReady?: (engine: FormEngine) => void;
  /** Called when this form unmounts, after onEngineReady's engine is no longer in use */
  onEngineDispose?: (engine: FormEngine) => void;
  /** Change this value to make the form validate itself and show its field errors (no submit) */
  validationRequest?: number;
  /** Called when a button control is clicked — pass button ID and form values */
  onButtonClick?: (buttonId: string, values: Record<string, any>) => void;
  /** When true, renders a <div> instead of <form> — use for child forms nested inside a FormContainer to avoid invalid nested <form> elements */
  nested?: boolean;
}

/**
 * FormRenderer component - renders complete form with validation error display
 *
 * Error Display Behavior:
 * - Field-level errors shown when field is touched (after blur)
 * - Validation summary shows all errors after failed submission
 * - Errors clear automatically on successful field input change
 * - Accessible to screen readers with proper ARIA attributes
 *
 * Visibility Behavior:
 * - Controls are filtered based on visibility rules on each value change
 * - Expression-based visibility is evaluated with form values
 * - Dependency-based visibility checks if dependent fields have values
 * - Mode-based visibility is applied based on operating mode
 */
export const FormRenderer: React.FC<FormRendererProps> = ({
  schema: rawSchema,
  initialValues,
  externalData,
  mode = 'edit',
  onSubmit,
  onClose,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  showValidationSummary = true,
  expressionEvaluator,
  onVisibilityChange,
  enableCsrfProtection = true,
  onValuesChange,
  onEngineReady,
  onEngineDispose,
  validationRequest,
  onButtonClick,
  nested = false,
}) => {
  // Resolve any inherited controls before the engine and renderer see the schema.
  // Fast-path: if no controls use inheritedFromForm this returns synchronously.
  const { schema, loading: inheritanceLoading } = useResolvedSchema(rawSchema, mode);

  // Fix #8 — initialize with rawSchema (always synchronously available).
  // When inheritance resolves, the existing schema-change useEffect below calls
  // engine.setSchema(resolvedSchema). The loading gate prevents any render before
  // that happens, so there is no observable double-init from the user's perspective.
  const [engine] = useState(() => new FormEngine(rawSchema, initialValues));
  // Report this form's engine for as long as it is mounted, so its container can validate it.
  // Depends only on the engine: the callbacks may change identity on every render.
  useEffect(() => {
    onEngineReady?.(engine);
    return () => onEngineDispose?.(engine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);
  const [values, setValues] = useState(engine.getValues());
  // Resolve control bindings (e.g. a Data Table bound to an expression) once per engine,
  // so bound values show in the preview. Without this, bindings were never applied.
  // Forms with no bindings skip the resolve entirely.
  useEffect(() => {
    if (!schemaHasBindings(rawSchema.controls)) return;
    let cancelled = false;
    engine
      .applyBindings(externalData)
      .then(() => {
        if (!cancelled) setValues(engine.getValues());
      })
      .catch(() => {
        // A failed binding leaves the control with its own value; the form still renders
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);
  const [errors, setErrors] = useState(engine.getErrors());
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<FormValidationResult | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [csrfReady, setCsrfReady] = useState(false);

  // Accessibility: Focus management for validation summary (Priority 1.3)
  const validationSummaryRef = React.useRef<HTMLDivElement>(null);

  // A container asked this form to show its errors: validate and touch every field, without submitting
  // The tick is kept in the registry, so a form that mounts later (e.g. a step revisited, or a step
  // whose errors were requested while it was not shown) validates itself on mount.
  const lastTickRef = React.useRef(0);
  useEffect(() => {
    if (!validationRequest || validationRequest === lastTickRef.current) return;
    lastTickRef.current = validationRequest;
    setValidationResult(engine.validate());
    setTouched(new Set((rawSchema.controls ?? []).map((c) => c.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationRequest]);

  // Compute mode configuration for visibility and interaction rules
  const modeConfig = useMemo(() => getModeConfig(mode), [mode]);

  // Keep engine in sync when schema or initialValues prop changes after mount
  // Issue #2 fix: Properly reset engine state, not just schema
  const schemaKeyRef = React.useRef(schema);
  const initialValuesRef = React.useRef(initialValues);
  useEffect(() => {
    const schemaChanged = schemaKeyRef.current !== schema;
    // Shallow VALUE equality, not `!==` reference equality — see isShallowEqualRecord's own
    // doc comment. Real, live-reproduced bug this fixes: an unstable-but-logically-unchanged
    // `initialValues` reference forced this effect to refire every render, which (via
    // onValuesChange below) fed back into some callers' own state, forcing another unstable
    // `initialValues` object next render — a genuine "Maximum update depth exceeded" infinite
    // loop, reproduced live through App Player's Documents Search FormWidget.
    const valuesChanged = !isShallowEqualRecord(initialValuesRef.current, initialValues);

    if (!schemaChanged && !valuesChanged) return; // skip on mount

    schemaKeyRef.current = schema;
    initialValuesRef.current = initialValues;

    // Reset engine completely with new schema and values
    engine.setSchema(schema);

    // Reset values if initialValues changed
    if (valuesChanged && initialValues) {
      Object.keys(initialValues).forEach(key => {
        engine.setFieldValue(key, initialValues[key]);
      });
    }

    setValues({ ...engine.getValues() });
    setErrors({ ...engine.getErrors() });
    setValidationResult(null);
    setTouched(new Set()); // Clear touched state on schema change
  }, [schema, initialValues, engine]);

  // Initialize CSRF token on form load
  useEffect(() => {
    if (enableCsrfProtection && mode !== 'view') {
      getCsrfToken()
        .then((token) => {
          setCsrfToken(token);
          setCsrfReady(true);
        })
        .catch((error) => {
          console.warn('CSRF token initialization failed — form submission may be rejected by the server:', error);
          setSubmitError('Security token could not be loaded. Submission may fail.');
          setCsrfReady(true); // Allow submission attempt anyway, server will reject if needed
        });
    } else {
      // CSRF not required or in view mode - mark as ready immediately
      setCsrfReady(true);
    }
  }, [enableCsrfProtection, mode]);

  // Memoize values object — use a ref-based comparison instead of JSON.stringify
  const prevValuesRef = React.useRef(values);
  const memoValues = useMemo(() => {
    const prev = prevValuesRef.current;
    // Shallow compare: if same keys and same values, reuse previous reference
    const keys = Object.keys(values);
    if (Object.keys(prev).length === keys.length && keys.every(k => prev[k] === values[k])) {
      return prev;
    }
    prevValuesRef.current = values;
    return values;
  }, [values]);

  // Initialize expression evaluator with stable reference
  const evaluator = useMemo(
    () => expressionEvaluator || new SafeExpressionEvaluator(),
    [expressionEvaluator]
  );

  // Phase 2 - Performance: Debounced values for visibility filtering
  // This prevents excessive visibility recalculation during rapid value changes (e.g., typing)
  const [debouncedValuesForVisibility, setDebouncedValuesForVisibility] = useState(memoValues);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValuesForVisibility(memoValues);
    }, 150); // 150ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [memoValues]);

  // Filter visible controls based on current values and mode
  // Note: Now uses debounced values to prevent excessive recalculation
  const filteredSchema = useMemo(() => {
    const filtered = filterSchemaByVisibility(schema, debouncedValuesForVisibility, mode, evaluator);
    return filtered;
  }, [schema, debouncedValuesForVisibility, mode, evaluator]);

  // Build parent map once per schema controls array change — used for style inheritance resolution.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parentMap = useMemo<ParentMap>(() => buildParentMap(schema), [schema.controls]);

  // Resolve form-level containerStyle for application to the root wrapper element.
  const formRootStyle = useMemo(() => {
    const s = schema.styles?.containerStyle;
    return s && !isEmptyStyleProperties(s) ? stylePropertiesToCSS(s) : {};
  }, [schema.styles]);


  // Phase 2 - Performance: Track visibility changes and notify parent with debouncing
  useEffect(() => {
    const visibleControlIds = filteredSchema.controls.map((c: { id: string }) => c.id);

    // Debounce the callback to prevent excessive parent notifications
    const timeoutId = setTimeout(() => {
      onVisibilityChange?.(visibleControlIds);
    }, 200); // 200ms debounce delay for notifications

    return () => clearTimeout(timeoutId);
  }, [filteredSchema, onVisibilityChange]);

  // Stable ref for onValuesChange to avoid re-subscribing on every render
  const onValuesChangeRef = React.useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;

  // Subscribe to form changes
  useEffect(() => {
    const unsubscribe = engine.on('change', () => {
      const latestValues = engine.getValues();
      setValues({ ...latestValues });
      setErrors({ ...engine.getErrors() });
      onValuesChangeRef.current?.(latestValues);
    });

    return unsubscribe;
  }, [engine]);

  // Publish the engine's merged values (persisted initialValues + each untouched
  // control's schema defaultValue) once when a new engine instance mounts. Without
  // this, onValuesChange only ever fires from the 'change' event above, which requires
  // the user to actually edit a field — so a field the user never touches (e.g. a
  // select left on its schema defaultValue) is shown pre-filled in the UI but is never
  // reported to the parent/store, and is silently dropped from what gets persisted on
  // save. Consumers already treat onValuesChange payloads as a full current-values
  // snapshot (not a diff), so an eager call here is consistent with the existing contract.
  useEffect(() => {
    onValuesChangeRef.current?.(engine.getValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  // Execute form-level JavaScript with trust level enforcement (Phase 3 - Security)
  useEffect(() => {
    if (!schema.metadata?.formJsBlock) return;

    const formJsBlock = schema.metadata.formJsBlock;
    const trustLevel = (schema.metadata as any).trustLevel ?? 0;

    // Create script executor with trust level enforcement
    const executor = new ScriptExecutor(5000); // 5 second timeout

    // Wrap the inline JS string in a JavaScriptBlock structure
    const scriptBlock = {
      scriptId: `form-${schema.metadata.formID}-inline-js`,
      name: `Inline JS for ${schema.metadata.title}`,
      code: formJsBlock,
      enabled: true,
    };

    // Create execution context with form data
    const context = {
      controlId: schema.metadata.formID,
      controlValue: null, // Not applicable for form-level scripts
      controlProps: {},
      formData: values,
    };

    // Execute script with trust level validation
    executor.executeScript(scriptBlock as any, context as any, trustLevel).catch((err) => {
      console.warn('[FormRenderer] Script execution blocked or failed:', err.message || err);
    });
  }, [schema.metadata?.formJsBlock, schema.metadata?.formID, schema.metadata?.title, values]);

  /**
   * Get field label by control ID
   * Used in validation summary
   */
  const getFieldLabel = useCallback((fieldId: string): string => {
    const control = schema.controls?.find((c) => c.id === fieldId);
    return control?.label || fieldId;
  }, [schema.controls]);

  /**
   * Build display-friendly error messages for validation summary
   */
  const displayErrors = useMemo(() => {
    if (!validationResult || validationResult.isValid) {
      return {};
    }

    // Convert ValidationError[] to Record<fieldId, message>
    const errorMap: Record<string, string> = {};
    validationResult.errors?.forEach((error) => {
      if (error.field && error.message) {
        errorMap[error.field] = error.message;
      }
    });
    return errorMap;
  }, [validationResult]);

  // Accessibility: Focus validation summary when errors appear (Priority 1.3)
  const errorCountRef = React.useRef(0);
  React.useEffect(() => {
    const currentErrorCount = Object.keys(displayErrors).length;
    const hadErrors = errorCountRef.current > 0;
    const hasNewErrors = currentErrorCount > 0 && currentErrorCount > errorCountRef.current;

    errorCountRef.current = currentErrorCount;

    // Focus validation summary when new errors appear (after form submission)
    if (hasNewErrors && validationSummaryRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        validationSummaryRef.current?.focus();
        validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [displayErrors]);

  const handleFieldChange = (fieldId: string, value: any) => {
    engine.setFieldValue(fieldId, value);
    // Clear validation result error for this field on change
    // This gives real-time feedback as user corrects the error
    if (validationResult?.fieldResults?.[fieldId]) {
      const newFieldResults = { ...validationResult.fieldResults };
      delete newFieldResults[fieldId];
      setValidationResult({
        ...validationResult,
        fieldResults: newFieldResults,
        errors: validationResult.errors.filter((e) => e.field !== fieldId),
      });
    }
  };

  const handleFieldBlur = (fieldId: string) => {
    engine.touchField(fieldId);
    setTouched((prev) => new Set([...prev, fieldId]));

    // Check the field that was left, so its error shows on blur rather than only after a submit.
    // Only this field's result changes; every other field keeps its current state.
    const checked = engine.validate();
    const fieldErrors = checked.errors.filter((error) => error.field === fieldId);
    setValidationResult((prev) => {
      const errors = [...(prev?.errors ?? []).filter((error) => error.field !== fieldId), ...fieldErrors];
      const fieldResults = { ...(prev?.fieldResults ?? {}) };
      if (checked.fieldResults?.[fieldId]) {
        fieldResults[fieldId] = checked.fieldResults[fieldId];
      } else {
        delete fieldResults[fieldId];
      }
      return { ...(prev ?? checked), errors, fieldResults, isValid: errors.length === 0 };
    });
  };

  // Child forms of every form-container in the schema are loaded here, not by the container. A
  // container inside a collapsed panel or an unvisited step is not mounted, but its children must
  // still be known so submit can validate them. Fallbacks read the live values of this form.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const childRefs = useMemo(() => containerChildFormRefs(schema.controls ?? []), [schema.controls]);
  const childRefsKey = JSON.stringify(childRefs);
  useEffect(() => {
    const refs = containerChildFormRefs(schema.controls ?? []);
    if (refs.length === 0) return;
    const registry = useChildFormRegistryStore.getState();
    for (const ref of refs) {
      registry.setFallback(childFormKey(ref.scope, ref.formID), () =>
        resolveChildData((valuesRef.current[ref.scope] as Record<string, unknown> | undefined) ?? {}, ref.binding)
      );
    }
    let cancelled = false;
    loadChildFormSchemas([...new Set(refs.map((ref) => ref.formID))]).then((loaded) => {
      if (cancelled) return;
      for (const ref of refs) {
        const schemaForForm = loaded.get(ref.formID);
        if (schemaForForm) registry.setSchema(childFormKey(ref.scope, ref.formID), schemaForForm);
      }
    });
    return () => {
      cancelled = true;
      for (const scope of new Set(refs.map((ref) => ref.scope))) registry.clearScope(scope);
    };
    // Re-run only when the set of containers or their bindings changes, not on every value edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childRefsKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Block submission until CSRF token is ready (if protection is enabled)
    if (enableCsrfProtection && mode !== 'view' && !csrfReady) {
      setSubmitError('Please wait - security initialization in progress...');
      return;
    }

    // Validate all fields
    const ownResult = engine.validate();
    // Child forms inside a FormContainer are validated by their own engines, so their required
    // fields must block this submit too.
    // Visible controls only: a hidden container is never mounted and must not block the submit
    const childErrors = collectChildFormErrors(filteredSchema.controls ?? []);
    const result: FormValidationResult = childErrors.length > 0
      ? { ...ownResult, isValid: false, errors: [...ownResult.errors, ...childErrors] }
      : ownResult;
    setValidationResult(result);

    // Mark all fields as touched to show all errors
    const allFieldIds = new Set(schema.controls?.map((c) => c.id) || []);
    setTouched(allFieldIds);

    if (!result.isValid) {
      return;
    }

    // Prepare submission headers with CSRF token
    const submissionHeaders: Record<string, string> = {};
    if (enableCsrfProtection && csrfToken) {
      submissionHeaders[getCsrfTokenHeaderName()] = csrfToken;
    }

    // Submit
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit?.(engine.getValues(), submissionHeaders);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Form submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (inheritanceLoading) {
    return (
      <div style={{ padding: '24px 16px', color: '#6b7879', fontSize: 14, textAlign: 'center' }}>
        Loading form definition…
      </div>
    );
  }

  // Form-scope controls are configuration — they must never be removed by the visibility
  // filter (which is designed for UI fields). Source them from the unfiltered schema.
  const { formScopeControls } = useFormScopePartition(schema.controls || []);

  // Field controls come from the visibility-filtered schema; strip any form-scope entries
  // that slipped through (e.g. if filterSchemaByVisibility preserved them).
  const fieldControls = useMemo(
    () => (filteredSchema.controls || []).filter(c => (c.scope ?? 'field') === 'field'),
    [filteredSchema.controls],
  );
  const errorCount = Object.keys(displayErrors).length;
  const hasErrors = errorCount > 0;

  const totalColumns = schema.metadata?.numberOfColumns ?? 1;
  const colVar = `repeat(${totalColumns}, 1fr)` as string;

  // ── Section grouping (real, reusable multi-column-per-section layout) ─────────────
  // Previously `schema.sections` was parsed/typed but never actually consulted by the
  // player — every control rendered into one flat `--af-cols`-wide grid regardless of
  // `sectionId`, so a form's declared sections (titles, grouping, per-section column
  // counts) were silently dropped at render time. Grouping controls by `sectionId` here
  // — using the already-built-but-previously-unused `FormSection` component — is fully
  // backward compatible: a schema with no `sections` (or whose controls don't reference
  // one) renders exactly as before, in the single flat grid.
  // `hideFilterArea` (FormMetadata) visually hides every section whose `role === 'filters'`
  // (see FormSection.role's doc comment) — the search term field, dropdown filters, and the
  // Search button itself — via CSS `display: none` on that section's wrapper, NOT by skipping
  // its controls at render time. This is deliberate: the controls stay fully mounted, wired to
  // real form state, and participate in the search payload exactly as before — so a host can
  // pre-populate a hidden filter's value (e.g. from a URL query param) and get a real,
  // working, pre-filtered "list" experience with no visible filter UI, rather than the value
  // simply not existing. (No pre-population mechanism exists in this codebase yet as of this
  // writing — see this feature's own history-log entry for that gap — but this CSS-only
  // approach doesn't block one from being added later.) A form with no `role: 'filters'`
  // section, or that doesn't set this flag, is unaffected.
  const hideFilterArea = schema.metadata?.hideFilterArea === true;
  const sortedSections = useMemo(
    () => [...(schema.sections ?? [])].sort((a, b) => a.order - b.order),
    [schema.sections],
  );
  const sectionIds = useMemo(() => new Set(sortedSections.map((s) => s.id)), [sortedSections]);

  const { unsectionedControls, controlsBySectionId } = useMemo(() => {
    const bySection = new Map<string, FormControl[]>();
    const unsectioned: FormControl[] = [];
    for (const control of fieldControls) {
      if (control.sectionId && sectionIds.has(control.sectionId)) {
        const arr = bySection.get(control.sectionId) ?? [];
        arr.push(control);
        bySection.set(control.sectionId, arr);
      } else {
        unsectioned.push(control);
      }
    }
    return { unsectionedControls: unsectioned, controlsBySectionId: bySection };
  }, [fieldControls, sectionIds]);

  /** Renders one control's grid item — shared by the flat path and every section's grid. */
  const renderControlItem = (control: FormControl) => {
    const gridStyle = getFieldGridStyle(control, totalColumns);
    const resolvedContainer = resolveStyles(control.id, 'containerStyle', schema, parentMap);
    const containerCSS = isEmptyStyleProperties(resolvedContainer) ? {} : stylePropertiesToCSS(resolvedContainer);
    // Grid styles spread last so layout positioning always wins over designer containerStyle
    const mergedStyle: React.CSSProperties = { ...containerCSS, ...gridStyle };

    return (
      <div key={control.id} className={`af-control af-control-${control.type} af-field-${control.id}`} style={mergedStyle}>
        <FieldErrorBoundary fieldId={control.id}>
          <FormField
            control={control}
            value={values[control.id]}
            error={displayErrors[control.id] || errors[control.id]}
            touched={touched.has(control.id)}
            mode={mode}
            onChange={(value) => handleFieldChange(control.id, value)}
            onBlur={() => handleFieldBlur(control.id)}
            onButtonClick={onButtonClick}
            formValues={values}
          />
        </FieldErrorBoundary>
      </div>
    );
  };

  const contentStyle = schema.metadata?.formContentStyle ?? 'input';
  const formIdClass = schema.metadata?.formID || '';
  const rendererClass = `af-form-renderer af-form-content-style-${contentStyle} form-user-mode-${mode}${formIdClass ? ` ${formIdClass}` : ''}`;

  // Nested child forms use a <div> to avoid invalid HTML nested <form> elements
  const Wrapper = nested ? 'div' : 'form';
  const wrapperProps = nested
    ? { className: rendererClass }
    : { onSubmit: handleSubmit, className: rendererClass, noValidate: true,
        'aria-label': `Form: ${schema.metadata?.title || 'Submission'}` };

  return (
    <FormOperatingModeProvider mode={mode}>
    <Wrapper {...wrapperProps as any} style={Object.keys(formRootStyle).length > 0 ? formRootStyle : undefined}>
      {/* Submission error banner */}
      {submitError && (
        <div role="alert" style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 12,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
          color: '#ef4444', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{submitError}</span>
          <button type="button" onClick={() => setSubmitError(null)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, padding: '0 4px' }}>×</button>
        </div>
      )}

      {/* Validation Summary - shown after failed submission (Priority 1.3 - Accessibility) */}
      {showValidationSummary && hasErrors && modeConfig.showValidation && (
        <div
          ref={validationSummaryRef}
          className="af-form-renderer__validation-summary"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          aria-label={`Form validation errors: ${errorCount} ${errorCount === 1 ? 'error' : 'errors'} found`}
          tabIndex={-1}
          style={{ outline: 'none' }}
        >
          <div className="af-form-renderer__validation-summary-header">
            <h3 className="af-form-renderer__validation-summary-title">
              Please fix {errorCount} validation {errorCount === 1 ? 'error' : 'errors'}
            </h3>
          </div>

          <ul className="af-form-renderer__validation-summary-list">
            {Object.entries(displayErrors).map(([fieldId, message]) => {
              const control = schema.controls?.find((c) => c.id === fieldId);
              const fieldLabel = control?.label || fieldId;

              return (
                <li key={fieldId} className="af-form-renderer__validation-summary-item">
                  <span className="af-form-renderer__validation-summary-label">{fieldLabel}</span>
                  <span className="af-form-renderer__validation-summary-message">{message}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Form Fields - only visible controls are rendered */}
      <FormScopeProcessor controls={formScopeControls} />
      {fieldControls.length > 0 ? (
        <>
          {/* Controls with no sectionId (or one not present in schema.sections) — unchanged
              flat single grid, exactly as every form rendered before section support existed. */}
          {unsectionedControls.length > 0 && (
            <div
              className="af-form-renderer__fields"
              style={{ '--af-cols': colVar } as React.CSSProperties}
            >
              {unsectionedControls.map(renderControlItem)}
            </div>
          )}

          {/* Sectioned controls — grouped under a titled FormSection, each with its own
              CSS grid column count (section.columns, defaulting to the form's numberOfColumns). */}
          {sortedSections.map((section) => {
            const sectionControls = controlsBySectionId.get(section.id) ?? [];
            if (sectionControls.length === 0) return null;
            const sectionCols = section.columns ?? totalColumns;
            const sectionColVar = `repeat(${sectionCols}, 1fr)` as string;
            // `hideFilterArea` (see this variable's own doc comment above): CSS `display: none`
            // only — the section (and every control inside it) stays fully mounted and wired
            // to real form state, so a hidden, pre-populated filter can still drive the search.
            const isHiddenFilterSection = hideFilterArea && section.role === 'filters';
            return (
              <div key={section.id} style={isHiddenFilterSection ? { display: 'none' } : undefined}>
                <FormSection section={section} controlCount={sectionControls.length}>
                  <div
                    className="af-form-renderer__fields"
                    style={{ '--af-cols': sectionColVar } as React.CSSProperties}
                  >
                    {sectionControls.map(renderControlItem)}
                  </div>
                </FormSection>
              </div>
            );
          })}
        </>
      ) : (
        <div className="af-form-renderer__no-fields" role="status" aria-live="polite">
          No visible fields in current context
        </div>
      )}

      {/* Form Actions — only for top-level forms (not nested), and only when the schema
          hasn't explicitly opted out via metadata.hideSubmitButton (search-and-results
          forms whose only real action is a schema-authored button, e.g. Documents Search,
          don't want the generic Submit bar — see FormMetadata.hideSubmitButton doc comment). */}
      {!nested && modeConfig.allowSubmit && !schema.metadata?.hideSubmitButton && (
        <div style={{
          display: 'flex', gap: 10, marginTop: 20, paddingTop: 16,
          borderTop: '1px solid #e5e7eb',
        }}>
          {onSubmit && (
            <button
              type="submit"
              // Keep focus where it is on mousedown. Otherwise the focused field blurs first, its
              // inline error appears and pushes this button down, and the click is cancelled.
              onMouseDown={(e) => e.preventDefault()}
              aria-busy={isSubmitting}
              disabled={isSubmitting || (enableCsrfProtection && mode !== 'view' && !csrfReady)}
              style={{
                padding: '9px 20px', borderRadius: 6,
                border: 'none', background: '#16a34a',
                color: '#fff', fontSize: 14, fontWeight: 600,
                cursor: (isSubmitting || (enableCsrfProtection && mode !== 'view' && !csrfReady)) ? 'not-allowed' : 'pointer',
                opacity: (isSubmitting || (enableCsrfProtection && mode !== 'view' && !csrfReady)) ? 0.7 : 1,
                transition: 'opacity 0.15s',
              }}
            >
              {isSubmitting ? 'Submitting…' : (!csrfReady && enableCsrfProtection && mode !== 'view') ? 'Initializing…' : submitLabel}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '9px 20px', borderRadius: 6,
                border: '1px solid #d1d5db', background: 'transparent',
                color: '#374151', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', transition: 'border-color 0.15s',
              }}
            >
              {cancelLabel}
            </button>
          )}
        </div>
      )}
    </Wrapper>
    </FormOperatingModeProvider>
  );
};

FormRenderer.displayName = 'FormRenderer';

/**
 * Error boundary for individual form fields.
 * Catches render errors in a single field without crashing the entire form.
 */
class FieldErrorBoundary extends React.Component<
  { fieldId: string; children: React.ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`FormField render error [${this.props.fieldId}]:`, error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '8px 12px', borderRadius: 6, fontSize: 13,
          background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', margin: '4px 0' }}>
          Field error: {this.state.error.message}
        </div>
      );
    }
    return this.props.children;
  }
}
